import { useState, MouseEvent } from "react"

import { alpha, TableRow } from "@mui/material"
import { Row } from "@tanstack/table-core"

import { JobTableRow, isJobGroupRow } from "../../../models/jobsTableModels"
import { Match } from "../../../models/lookoutModels"

import { BodyCell } from "./JobsTableCell"

export interface JobsTableRowProps {
  row: Row<JobTableRow>
  isOpenInSidebar: boolean
  onClick?: (e: MouseEvent<HTMLTableRowElement>) => void
  onClickRowCheckbox: (row: Row<JobTableRow>) => void
  onColumnMatchChange: (columnId: string, newMatch: Match) => void
}

export const JobsTableRow = ({
  row,
  isOpenInSidebar,
  onClick,
  onClickRowCheckbox,
  onColumnMatchChange,
}: JobsTableRowProps) => {
  // Helpers to avoid triggering onClick if the user is selecting text
  const [{ pageX, pageY }, setPagePosition] = useState({ pageX: -1, pageY: -1 })
  const isDragging = (e: MouseEvent) => {
    const dragThresholdPixels = 6
    const diffX = Math.abs(e.pageX - pageX)
    const diffY = Math.abs(e.pageY - pageY)
    return diffX > dragThresholdPixels || diffY > dragThresholdPixels
  }

  const original = row.original
  const rowIsGroup = isJobGroupRow(original)
  const rowCells = row.getVisibleCells()
  return (
    <TableRow
      aria-label={row.id}
      sx={{
        backgroundSize: row.depth * 6,
        backgroundImage: (theme) => `linear-gradient(${theme.palette.primary.main}, ${theme.palette.primary.main})`,
        backgroundRepeat: "no-repeat",

        ...(onClick && {
          cursor: "pointer",
        }),

        ...(isOpenInSidebar && {
          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.35),
        }),

        "&:hover": {
          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.2),
        },
      }}
      onMouseDown={(e) => setPagePosition(e)}
      onClick={(e) => {
        if (!isDragging(e) && onClick) {
          onClick(e)
        }
      }}
    >
      {rowCells.map((cell) => (
        <BodyCell
          cell={cell}
          rowIsGroup={rowIsGroup}
          rowIsExpanded={row.getIsExpanded()}
          onExpandedChange={row.toggleExpanded}
          onClickRowCheckbox={onClickRowCheckbox}
          onColumnMatchChange={onColumnMatchChange}
          key={cell.id}
        />
      ))}
    </TableRow>
  )
}
